<template>
  <div class="pdp-product-details">
    <input id="product-name" type="hidden" :value="product.name" />
    <input id="product-code" type="hidden" :value="variant.code" />
    <input id="product-brand" type="hidden" :value="variant.brandNameFull" />
    <section
      v-if="!purchaseMode || product.isArchived"
      id="buying-zone"
      class="b2b-buying-zone grid-container pt-4"
      data-pdp
    >
      <pdp-product-header :product="product" :base-properties="baseProperties" />
      <div class="b2b-buying-zone__content grid-x grid-margin-x mt-3 md:mt-5">
        <div class="cell medium-12 large-8">
          <div
            class="b2b-buying-zone__carousel-container buying-zone__carousel mb-3 md:mb-5"
            :data-brand-name="product.brandNameFull"
          >
            <!-- Product Image Panel -->
            <pdp-product-image-panel
              v-if="hasImages"
              :product="product"
              :color="selectedColor"
              :base-properties="baseProperties"
              :max-thumbnails="options.maxPdpThumbnails"
            />
          </div>
        </div>
        <div class="cell medium-12 large-4">
          <div class="b2b-buying-zone__info-options-container grid-x grid-margin-x">
            <div class="b2b-buying-zone__info cell medium-6 large-12">
              <div data-product-details>
                <!-- Product Price -->
                <pdp-product-price
                  :product="product"
                  :is-variant-selected="!!variantCode"
                  :retailer="retailerPrice"
                  :consumer="consumerPrice"
                  :base-properties="baseProperties"
                />
                <pdp-reviews-summary class="mt-2 buying-zone" :product="product" />
                <span class="pr-separator"> / </span>
                <pdp-product-summary :product="product" />
              </div>
              <div
                v-if="copyPositioningStatement.length > 0"
                class="buying-zone__positioning-statement mb-2 pt-2 border-t border-gray-10"
              >
                <p qaid="product-positioning-statement" v-html="copyPositioningStatement" />
              </div>
            </div>

            <footer class="b2b-buying-zone__options cell medium-6 large-12 lg:pt-2">
              <!-- Attributes Selection -->
              <pdp-product-attributes-container
                ref="attributesContainer"
                v-model="variantCode"
                :product="product"
                :init-attribute-selection="initAttributeSelection"
                :base-properties="baseProperties"
                @color-changed="
                  (color) => {
                    selectedColor = color;
                  }
                "
              />
              <!-- Display SKU of Selected Variant -->
              <div
                v-if="variantCode && variantCode.length > 0"
                class="pdp-sku-number flex justify-between my-2 items-center"
              >
                <div />
                <div qaid="buying-zone.sku" class="flex">
                  <span class="buying-zone__code text-secondary" v-html="buyingZoneCode"></span>
                  <trek-link
                    v-show="options.hasUserAccessToBuyingZone"
                    button
                    text
                    icon-only
                    icon="history"
                    :href="orderHistoryUrl"
                    class="ml-1"
                    qaid="buying-zone.orderHistory"
                    aria-label="Order history"
                  >
                  </trek-link>
                </div>
              </div>

              <div v-if="product.isArchived">
                <div v-if="product.archivedProductCurrentCategoryLink" class="mt-2">
                  <trek-link button primary block :href="product.archivedProductCurrentCategoryLink">
                    <span v-html="bp('seeCurrent')" />
                  </trek-link>
                </div>
                <pdp-replacement
                  v-if="variantCode"
                  :replacement-product="replacementProduct"
                  :base-properties="baseProperties"
                />
              </div>
              <div v-else>
                <callout v-if="variant.replacementProduct">
                  <span v-html="replacementProductLink" />
                </callout>
                <!-- Display Availability -->
                <pdp-product-warehouses
                  v-show="!isSpectrumEnabled && options.hasUserAccessToBuyingZone"
                  v-model="product.code"
                  :product-type="product.productType"
                  :base-properties="baseProperties"
                  @availability-change="onAvailabilityChange"
                />
                <!-- Add to Cart -->
                <pdp-customize-product-button
                  :base-properties="baseProperties"
                  :project-one-url="projectOneUrl"
                  :spectrum-url="product.spectrumUrl"
                  :custom-configurator-type="product.customConfiguratorType"
                  :is-project-one-configurator-active="options.isProjectOneConfiguratorActive"
                  :custom-product-starting-point="product.customProductStartingPoint"
                  :site-locale="options.siteLocale"
                  :options="options"
                />
                <pdp-add-to-cart
                  v-show="!isSpectrumEnabled"
                  :base-properties="baseProperties"
                  :options="options"
                  :product="product"
                  :variant-code="variantCode"
                  :is-adding-to-cart="isAddingToCart"
                  :is-purchasable="hasPrice && hasAvailability"
                  :min-quantity="minQuantity"
                  :has-availability="hasAvailability"
                  :add-to-cart-button-caption="addToCartButtonCaption"
                  :success-message="cartSuccessMessage"
                  :has-user-access-to-buying-zone="options.hasUserAccessToBuyingZone"
                  @add-to-cart="
                    (quantity) => {
                      addToCart(variantCode, quantity);
                    }
                  "
                />
              </div>
              <pdp-rpa-results
                v-if="canShowRpa && options.rpaFunctionalityActive"
                :is-bike="isBike"
                :base-properties="baseProperties"
                :stock-data="stockData"
                :is-location-postal-code-correct="isLocationPostalCodeCorrect"
                :options="options"
                :product="product"
                @updateRpaStockStatus="loadNewRpaResults"
              />
            </footer>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
<script>
import {mapActions, mapState, mapGetters} from 'vuex';
import {InitProductAttributes} from '@/components/containers/pdp/utils/init-product-attributes';
import get from 'lodash/get';
import {AddToCart} from '@/components/containers/pdp/utils/add-to-cart';
import {ProductStockData} from '@/components/containers/pdp/utils/product-stock-data';
import PdpProductHeader from '@/components/containers/pdp/b2b/PdpProductHeader';
import PdpReviewsSummary from '@/components/containers/pdp/PdpReviewsSummary';
import PdpProductSummary from '@/components/containers/pdp/PdpProductSummary';
import PdpProductPrice from '@/components/containers/pdp/PdpProductPrice';
import PdpProductAttributesContainer from '@/components/containers/pdp/PdpProductAttributesContainer';
import PdpProductWarehouses from '@/components/containers/pdp/b2b/PdpProductWarehouses';
import PdpAddToCart from '@/components/containers/pdp/b2b/PdpAddToCart';
import PdpProductImagePanel from '@/components/containers/pdp/PdpProductImagePanel';
import {ProductDetails} from '@/components/containers/pdp/utils/product-details';
import PdpRpaResults from '@/components/containers/pdp/PdpRpaResults';
import PdpReplacement from '@/components/containers/pdp/PdpReplacement';
import TrekLink from '@/components/TrekLink';
import Callout from '@/components/Callout.vue';
import PdpCustomizeProductButton from '@/components/containers/pdp/PdpCustomizeProductButton';

export default {
  name: 'PdpProductDetails',
  components: {
    PdpReplacement,
    PdpRpaResults,
    PdpProductImagePanel,
    PdpAddToCart,
    PdpProductWarehouses,
    PdpProductAttributesContainer,
    PdpProductPrice,
    PdpProductSummary,
    PdpReviewsSummary,
    PdpProductHeader,
    TrekLink,
    Callout,
    PdpCustomizeProductButton,
  },
  mixins: [AddToCart, InitProductAttributes, ProductStockData, ProductDetails],
  data() {
    return {
      hasAvailability: false,
    };
  },
  computed: {
    orderHistoryUrl() {
      return `${this.encodedContextPath}/my-account/orders?partNumber=${this.variantCode}`;
    },
    minQuantity() {
      return get(this.pricingData, 'vendorMinQty', get(this.product, 'vendorMinQty', 0));
    },
    replacementProduct() {
      if (!this.variantCode) {
        return null;
      }

      return get(
        this.product.variants.find((variant) => variant.code === this.variantCode),
        'replacementProduct'
      );
    },
    replacementProductLink() {
      if (!this.replacementProduct.url) {
        return null;
      }
      return this.bp('replacementAvailable', this.replacementProduct.url);
    },
    canShowRpa() {
      if (!this.product) {
        return false;
      }

      const allowedByOptions = !get(this.options, 'kioskModeEnabledOnSite');
      const hasRetailers = get(this.stockData, 'retailers', []).length > 0;
      const isVariantSelected = this.variantCode && this.variantCode.length > 0;

      return allowedByOptions && hasRetailers && isVariantSelected;
    },
    buyingZoneCode() {
      return `${this.bp('sku')} ${this.variantCode}`;
    },
    isSpectrumEnabled() {
      return !!this.product.spectrumUrl;
    },
    ...mapState('backend', ['encodedContextPath']),
    ...mapState('pdp', ['pdpDetailsLoaded']),
    ...mapGetters('storage', ['purchaseMode']),
  },
  watch: {
    variantCode(code) {
      const productCode = !code ? this.product.code : code;
      this.setVariantPdpSku(productCode);
      this.loadPricing(code);
    },
  },
  mounted() {
    // Set base product's ID for B2B section only.
    this.setVariantPdpSku(this.product.code);
    window.addEventListener('load', this.pdpFullyLoaded());
  },
  beforeDestroy() {
    window.removeEventListener('load', this.pdpFullyLoaded());
  },
  methods: {
    ...mapActions('pdp', ['setVariantPdpSku']),
    ...mapActions('pdp', ['updatePdpDetailsLoaded']),
    onAvailabilityChange(status) {
      this.hasAvailability = status;
    },
    loadNewRpaResults(code, product) {
      this.$emit('updateRpaResults', code, product);
    },
    pdpFullyLoaded() {
      this.updatePdpDetailsLoaded(true);
    },
  },
};
</script>
